import { inject } from '@angular/core';
import type { CanActivateFn, UrlTree } from '@angular/router';
import { AppPathsMap } from '@core/enums/app-paths';
import { NavigationService } from '@core/navigation/services/navigation.service';
import { AmlAdminErrorHandlerService } from '@core/services/aml-admin-error-handler.service';
import { environment } from '@env';
import type { NamedRole } from '@kerberos-compliance/compliance-shared-ts/core/iam/authorization/enums/named-role';
import { ErrorSeverityMap } from '@kerberos-compliance/compliance-shared-ui';
import { AuthorizationFacade } from '../facades/authorization.facade';

/**
 * A function that defines a guard to protect routes based on user authorization.
 * This function uses a required role to determine if the user has the necessary access.
 * If the user has the required role, access to the route is granted.
 * If not, the user is redirected to an unauthorized page.
 *
 * Behavior:
 * - If the application is in E2E mode or authentication is disabled, access is automatically granted.
 * - If the route does not specify required roles, access is granted by default.
 * - Grants are dynamically loaded and compared against the required roles to determine access.
 * - Errors during grant loading are logged, and the user is redirected to the unauthorized page.
 *
 * Dependencies:
 * - Uses `AuthorizationFacade` to load user grants and check roles.
 * - Uses `NavigationService` to build the redirect `UrlTree` to the unauthorized page if access is denied.
 * - Uses `AmlAdminErrorHandlerService` to handle and log any errors encountered.
 *
 * @param route The route being accessed, which may include metadata specifying required roles.
 * @returns A Promise resolving to a boolean indicating access permission,
 * or a `UrlTree` redirecting to the unauthorized page.
 */
export const authorizationGuard: CanActivateFn = async (route): Promise<boolean | UrlTree> => {
  if (environment.e2eMode || !environment.enableMsal) {
    return true;
  }
  const requiredRoles = route.data?.['roles'] as NamedRole[] | undefined;
  if (!requiredRoles?.length) return true;

  const authorizationFacade = inject(AuthorizationFacade);
  const navigationService = inject(NavigationService);
  const errorHandlerService = inject(AmlAdminErrorHandlerService);

  try {
    // Wait for the user grants to be loaded
    await authorizationFacade.loadGrants();

    return requiredRoles.some((role) => authorizationFacade.hasRole(role))
      ? true
      : navigationService.createUrlTree([AppPathsMap.Unauthorized]);
  } catch (error) {
    errorHandlerService.handleError({
      error,
      metadata: {
        severity: ErrorSeverityMap.High,
        component: 'authorizationGuard',
        action: 'Error retrieving user grants.',
      },
    });
    return navigationService.createUrlTree([AppPathsMap.Unauthorized]);
  }
};
